import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import logo from "assets/logo/logo.png";
import { db } from "firebase.js";
import { collection, getDocs } from "firebase/firestore";
import AdminControlCell from "Components/Admin/AdminControlCell";
import LocationManager from "Components/Admin/LocationManager";
import { updateUser } from "services/userService";
import { getAllLocations } from "services/locationService";

const TABS = [
  { key: "utilisateurs", label: "Utilisateurs" },
  { key: "lieux", label: "Lieux" },
];

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [locations, setLocations] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === searchParams.get("tab"))
    ? searchParams.get("tab")
    : TABS[0].key;

  useEffect(() => {
    const fetchUsers = async () => {
      const querySnapshot = await getDocs(collection(db, "users"));
      const usersList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(usersList);
      setFilteredUsers(usersList);
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    let results = [...users];

    if (filter === "admin") {
      results = results.filter((u) => u.adminLocations?.length > 0);
    } else if (filter === "master") {
      results = results.filter((u) => u.master);
    }

    if (searchQuery) {
      const lower = searchQuery.toLowerCase();
      results = results.filter(
        (u) =>
          u.name?.toLowerCase().includes(lower) ||
          u.email?.toLowerCase().includes(lower)
      );
    }

    setFilteredUsers(results);
  }, [searchQuery, filter, users]);

  // Shared by the admin assignment dropdown and the locations tab. Discarded locations are
  // included so their ids stay taken and old admin rights still show their name; each
  // view hides them.
  const refreshLocations = useCallback(async () => {
    setLocations(await getAllLocations({ includeDiscarded: true }));
  }, []);

  useEffect(() => {
    refreshLocations();
  }, [refreshLocations]);

  const refreshUsers = async () => {
    const snap = await getDocs(collection(db, "users"));
    setUsers(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
  };

  return (
    <div className="min-h-screen bg-gray-700 p-6 text-white">
      {/* The admin pages have no navbar: a way back to the site */}
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 mb-4">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-white transition focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 rounded"
        >
          <ArrowLeft size={18} aria-hidden="true" />
          Retour à l'accueil
        </Link>
        <Link
          to="/"
          aria-label="FQST, page d'accueil"
          className="rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
        >
          <img src={logo} alt="" className="h-10" />
        </Link>
      </div>

      <h2 className="text-4xl font-semibold text-center mb-6">
        {tab === "lieux" ? "Gestion des Lieux" : "Gestion des Utilisateurs"}
      </h2>

      <div className="max-w-7xl mx-auto mb-4 flex gap-2 border-b border-gray-600" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setSearchParams(t.key === TABS[0].key ? {} : { tab: t.key })}
            className={`px-5 py-2 -mb-px border-b-2 font-semibold transition ${
              tab === t.key
                ? "border-amber-400 text-amber-300"
                : "border-transparent text-gray-300 hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "lieux" ? (
        <div className="max-w-7xl mx-auto bg-gray-800 p-6 rounded-lg shadow-xl">
          <LocationManager
            locations={locations}
            refreshLocations={refreshLocations}
            users={users}
          />
        </div>
      ) : (
      <div className="max-w-7xl mx-auto bg-gray-800 p-6 rounded-lg shadow-xl space-y-4">
        {/* Filters + Search */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <input
            type="text"
            placeholder="Rechercher par nom ou email"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="px-4 py-2 rounded-md w-full md:w-1/2 bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-amber-400"
          />

          <div className="flex space-x-2">
            <button
              onClick={() => setFilter("all")}
              className={`px-4 py-2 rounded ${
                filter === "all" ? "bg-amber-600" : "bg-gray-700"
              } hover:bg-amber-500 transition`}
            >
              Tous
            </button>
            <button
              onClick={() => setFilter("admin")}
              className={`px-4 py-2 rounded ${
                filter === "admin" ? "bg-amber-600" : "bg-gray-700"
              } hover:bg-amber-500 transition`}
            >
              Admins
            </button>
            <button
              onClick={() => setFilter("master")}
              className={`px-4 py-2 rounded ${
                filter === "master" ? "bg-amber-600" : "bg-gray-700"
              } hover:bg-amber-500 transition`}
            >
              Masters
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-gray-600 text-amber-300">
                <th className="p-2">Nom</th>
                <th className="p-2">Date de création</th>
                <th className="p-2">Email</th>
                <th className="p-2">Téléphone</th>
                <th className="p-2">Admin des lieux</th>
                <th className="p-2">Master</th>
                <th className="p-2">Suspendre</th>
                <th
                  className="p-2"
                  title="Cocher permet à l'utilisateur de se connecter et de s'inscrire aux tournois même s'il n'a jamais reçu le courriel de confirmation."
                >
                  Vérifié
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id} className="border-b border-gray-600">
                  <td className="p-2">{user.name || <i>—</i>}</td>
                  <td className="p-2">
                    {user.createdAt ? (
                      new Date(
                        user.createdAt.seconds * 1000
                      ).toLocaleDateString("fr-FR", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                      })
                    ) : (
                      <i>—</i>
                    )}
                  </td>
                  <td className="p-2">{user.email}</td>
                  <td className="p-2">{user.phone}</td>
                  <AdminControlCell
                    user={user}
                    locations={locations}
                    refreshUsers={refreshUsers}
                  />
                  <td className="p-2">
                    {user.master ? (
                      <span className="text-orange-400">Oui</span>
                    ) : (
                      <span className="text-gray-400 italic">Non</span>
                    )}
                  </td>
                  <td className="p-2 text-center">
                    <input
                      type="checkbox"
                      checked={!!user.onHold}
                      onChange={async (e) => {
                        await updateUser(user.id, { onHold: e.target.checked });
                        refreshUsers(); // Refresh after update
                      }}
                    />
                  </td>
                  <td className="p-2 text-center">
                    <input
                      type="checkbox"
                      checked={!!user.verified}
                      onChange={async (e) => {
                        await updateUser(user.id, { verified: e.target.checked });
                        refreshUsers(); // Refresh after update
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
};

export default AdminDashboard;
